import { useState } from 'react';
import {
  calendarStatisticsPeriodLabels,
  calendarStatisticsPeriods,
  formatInitials,
  type CalendarStatisticsPeriod,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Skeleton, Tabs } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { CalendarPage } from './CalendarModule';
import { useCalendarStatistics, type CalendarStatistics } from './calendarApi';
import styles from './StatisticsPage.module.css';

const periodText: Record<CalendarStatisticsPeriod, string> = {
  '7d': 'der letzten 7 Tage',
  '30d': 'der letzten 30 Tage',
  '90d': 'der letzten 90 Tage',
  '1y': 'des letzten Jahres',
};

function percent(value: number | null): string {
  return value === null ? '–' : `${Math.round(value)} %`;
}

/** „404 (98 %)"; without a percentage only the count. */
function countWithPct(count: number | null, pct: number | null): string {
  if (count === null) return '–';
  return pct === null ? String(count) : `${count} (${Math.round(pct)} %)`;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return formatInitials(parts[0] ?? '', parts.length > 1 ? (parts[parts.length - 1] ?? '') : '');
}

function Kpis({ data }: { data: CalendarStatistics }) {
  const { kpis } = data;
  return (
    <div className={styles.kpis}>
      <div className={styles.kpi}>
        <div className={styles.kpiLabel}>Buchungen</div>
        <div className={styles.kpiValue}>{kpis.bookings}</div>
        <div className={styles.kpiSub}>{kpis.viaBookingPage} über Buchungsseite</div>
      </div>
      <div className={styles.kpi}>
        <div className={styles.kpiLabel}>Bevorstehend</div>
        <div className={styles.kpiValue}>{kpis.upcoming}</div>
        <div className={styles.kpiSub}>{kpis.upcomingThisWeek} diese Woche</div>
      </div>
      <div className={styles.kpi}>
        <div className={styles.kpiLabel}>Storniert</div>
        <div className={styles.kpiValue}>{kpis.cancelled}</div>
        <div className={styles.kpiSub}>{percent(kpis.cancelledPct)} der Buchungen</div>
      </div>
      <div className={clsx(styles.kpi, styles.kpiDark)}>
        <div className={styles.kpiLabel}>Show-Quote</div>
        <div className={styles.kpiValue}>{percent(kpis.showRate)}</div>
        <div className={styles.kpiSub}>
          {kpis.attended} erschienen · {kpis.noShow} No-Show
        </div>
      </div>
    </div>
  );
}

function Delivery({ data }: { data: CalendarStatistics }) {
  return (
    <section className={styles.card} aria-labelledby="statistics-delivery">
      <h2 id="statistics-delivery" className={styles.cardTitle}>
        Zustellung an Mandanten
      </h2>
      <div className={styles.deliveryTable} role="table" aria-labelledby="statistics-delivery">
        <div className={styles.deliveryRow} role="row">
          {['Kanal', 'Gesendet', 'Zugestellt', 'Geöffnet', 'Fehler'].map((label) => (
            <span key={label} className={styles.th} role="columnheader">
              {label}
            </span>
          ))}
        </div>
        {data.delivery.map((row) => (
          <div key={row.channel} className={styles.deliveryRow} role="row">
            <span role="cell">{row.channel}</span>
            <span role="cell">{row.sent}</span>
            <span role="cell">{countWithPct(row.delivered, row.deliveredPct)}</span>
            <span role="cell" className={row.opened === null ? styles.dash : undefined}>
              {countWithPct(row.opened, row.openedPct)}
            </span>
            <span role="cell">{row.failed}</span>
          </div>
        ))}
      </div>
      <p className={styles.footnote}>
        Öffnungen werden über ein Zählpixel in HTML-Mails erfasst; Programme mit Bildblockierung zählen nicht.
      </p>
    </section>
  );
}

function Team({
  data,
  organizationName,
  viewerId,
}: {
  data: CalendarStatistics;
  organizationName: string;
  viewerId: string;
}) {
  const count = data.team.length;
  return (
    <section className={styles.card} aria-labelledby="statistics-team">
      <div className={styles.teamHead}>
        <h2 id="statistics-team" className={styles.cardTitle}>
          Team
        </h2>
        <span className={styles.teamMeta}>
          {organizationName} · {count} {count === 1 ? 'Mitglied' : 'Mitglieder'}
        </span>
      </div>
      <ul className={styles.teamList}>
        {data.team.map((member) => (
          <li key={member.userId} className={styles.teamRow}>
            <span
              className={clsx(styles.avatar, member.userId === viewerId && styles.avatarViewer)}
              aria-hidden="true"
            >
              {initialsOf(member.name)}
            </span>
            <span className={styles.teamName}>
              <span className={styles.teamNameText}>{member.name}</span>
              <span className={styles.teamEmail}>{member.email}</span>
            </span>
            <span className={styles.teamFigures}>
              {member.appointments} {member.appointments === 1 ? 'Termin' : 'Termine'} ·{' '}
              {percent(member.showRate)} Show
            </span>
            <span className={clsx(styles.rolePill, member.roleLabel === 'Admin' && styles.rolePillAdmin)}>
              {member.roleLabel}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Beratung 09 — `/calendar/statistics`: KPIs, delivery per channel, team; the scope follows `controlling.figures`. */
export function StatisticsPage() {
  const workspace = useWorkspace();
  const [period, setPeriod] = useState<CalendarStatisticsPeriod>('30d');
  const statistics = useCalendarStatistics(period);
  const data = statistics.data;
  const status = statistics.error instanceof ApiError ? statistics.error.status : null;
  const forbidden = status === 403;

  return (
    <CalendarPage tab="statistics">
      <div className={styles.head}>
        <div>
          <h2 className={styles.title}>Statistik</h2>
          <div className={styles.description}>
            Buchungen, Show-Quote und Zustellung {periodText[period]}
            {data ? ` · ${data.scopeLabel}` : ''}
          </div>
        </div>
        <Tabs
          label="Zeitraum"
          variant="bare"
          className={styles.periods}
          items={calendarStatisticsPeriods.map((value) => ({
            value,
            label: calendarStatisticsPeriodLabels[value],
          }))}
          value={period}
          onChange={setPeriod}
        />
      </div>
      {forbidden ? (
        <Banner tone="neutral" className={styles.banner}>
          {errorMessage(statistics.error)}
        </Banner>
      ) : statistics.isError && !data ? (
        <Banner
          tone="danger"
          className={styles.banner}
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={statistics.isFetching}
              onClick={() => void statistics.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Die Statistik konnte nicht geladen werden{status !== null ? ` · Fehler ${status}` : ''}.{' '}
          {errorMessage(statistics.error)}
        </Banner>
      ) : !data ? (
        <div className={styles.skeleton} aria-busy="true" aria-label="Statistik wird geladen">
          <div className={styles.kpis}>
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height={130} />
            ))}
          </div>
          <div className={styles.columns}>
            <Skeleton height={220} />
            <Skeleton height={220} />
          </div>
        </div>
      ) : (
        <div className={clsx(styles.content, statistics.isPlaceholderData && styles.stale)}>
          <Kpis data={data} />
          <div className={styles.columns}>
            <Delivery data={data} />
            <Team data={data} organizationName={workspace.organizationName} viewerId={workspace.user.id} />
          </div>
        </div>
      )}
    </CalendarPage>
  );
}
